import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";
import {
  EVENT_CATEGORIES,
  EVENT_LANGUAGES,
  categoryLabel,
  genresFor,
  subcategoriesFor,
} from "./eventCategories.js";

function useClickOutside(ref, onOutside) {
  useEffect(() => {
    const handler = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      onOutside?.();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onOutside]);
}

function Pill({ label, onRemove, disabled }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 px-2.5 py-1 text-sm font-semibold text-white">
      {label}
      {!disabled && (
        <button
          type="button"
          aria-label={`Remove ${label}`}
          className="rounded-full p-0.5 hover:bg-amber-600"
          onClick={onRemove}
        >
          <FaTimes className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

/** Single-select: shows pill when chosen, else searchable input. */
function SingleSearchSelect({
  label,
  required,
  placeholder,
  options,
  value,
  onSelect,
  onClear,
  disabled,
  error,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);
  useClickOutside(boxRef, () => setOpen(false));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => {
      const text = typeof opt === "string" ? opt : opt.label;
      return String(text).toLowerCase().includes(q);
    });
  }, [options, query]);

  const selectedLabel =
    value &&
    (typeof options[0] === "string"
      ? value
      : options.find((o) => o.value === value)?.label || categoryLabel(value));

  return (
    <div ref={boxRef} className="relative">
      <label className="mb-1.5 block text-sm font-semibold text-slate-700">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </label>

      {value ? (
        <div
          className={`flex min-h-[44px] flex-wrap items-center gap-2 rounded-lg border bg-white px-3 py-2 ${
            error ? "border-rose-400" : "border-slate-200"
          } ${disabled ? "opacity-60" : ""}`}
        >
          <Pill
            label={selectedLabel}
            disabled={disabled}
            onRemove={() => {
              onClear?.();
              setQuery("");
              setOpen(false);
            }}
          />
        </div>
      ) : (
        <div
          className={`relative flex min-h-[44px] items-center rounded-lg border bg-white ${
            error
              ? "border-rose-400"
              : open
                ? "border-amber-400 ring-2 ring-amber-100"
                : "border-slate-200"
          } ${disabled ? "opacity-60" : ""}`}
        >
          <input
            type="text"
            disabled={disabled}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="w-full rounded-lg bg-transparent px-3 py-2.5 pr-10 text-sm outline-none placeholder:text-slate-400"
            aria-label={label}
            autoComplete="off"
          />
          <FaSearch className="pointer-events-none absolute right-3 h-3.5 w-3.5 text-slate-400" />
        </div>
      )}

      {error && <p className="mt-1 text-xs font-medium text-rose-600">{error}</p>}

      {open && !value && !disabled && (
        <ul className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {filtered.length === 0 ? (
            <li className="px-3 py-2.5 text-sm text-slate-500">No matches</li>
          ) : (
            filtered.map((opt) => {
              const optValue = typeof opt === "string" ? opt : opt.value;
              const optLabel = typeof opt === "string" ? opt : opt.label;
              return (
                <li key={optValue}>
                  <button
                    type="button"
                    className="w-full px-3 py-2.5 text-left text-sm text-slate-800 hover:bg-amber-50"
                    onClick={() => {
                      onSelect(optValue);
                      setQuery("");
                      setOpen(false);
                    }}
                  >
                    {optLabel}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}

/** Multi-select with pills + always-visible search (BMS style). */
function MultiSearchSelect({
  label,
  required,
  placeholder,
  options,
  values = [],
  onChange,
  disabled,
  error,
  emptyHint,
  max,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);
  useClickOutside(boxRef, () => setOpen(false));

  const selected = Array.isArray(values) ? values : [];
  const atMax = typeof max === "number" && selected.length >= max;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter((opt) => {
      const text = typeof opt === "string" ? opt : opt.label;
      const val = typeof opt === "string" ? opt : opt.value;
      if (selected.includes(val)) return false;
      if (!q) return true;
      return String(text).toLowerCase().includes(q);
    });
  }, [options, query, selected]);

  const add = (val) => {
    if (atMax || selected.includes(val)) return;
    onChange([...selected, val]);
    setQuery("");
  };

  const remove = (val) => onChange(selected.filter((v) => v !== val));

  return (
    <div ref={boxRef} className="relative">
      <label className="mb-1.5 block text-sm font-semibold text-slate-700">
        {label}
        {required && <span className="text-rose-500"> *</span>}
        {typeof max === "number" && (
          <span className="ml-1 font-normal text-slate-400">(upto {max})</span>
        )}
      </label>

      <div
        className={`rounded-lg border bg-white px-3 py-2 ${
          error ? "border-rose-400" : open ? "border-amber-400 ring-2 ring-amber-100" : "border-slate-200"
        } ${disabled ? "opacity-60" : ""}`}
      >
        {selected.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {selected.map((val) => {
              const opt = options.find((o) =>
                typeof o === "string" ? o === val : o.value === val
              );
              const pillLabel =
                typeof opt === "string" ? opt : opt?.label || val;
              return (
                <Pill
                  key={val}
                  label={pillLabel}
                  disabled={disabled}
                  onRemove={() => remove(val)}
                />
              );
            })}
          </div>
        )}

        {!atMax && (
          <div className="relative">
            <input
              type="text"
              disabled={disabled}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              className="w-full bg-transparent py-1.5 pr-8 text-sm outline-none placeholder:text-slate-400"
              aria-label={label}
              autoComplete="off"
            />
            <FaSearch className="pointer-events-none absolute right-0 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          </div>
        )}
      </div>

      {error && <p className="mt-1 text-xs font-medium text-rose-600">{error}</p>}

      {open && !disabled && !atMax && (
        <ul className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {filtered.length === 0 ? (
            <li className="px-3 py-2.5 text-sm text-slate-500">
              {emptyHint || "No matches"}
            </li>
          ) : (
            filtered.map((opt) => {
              const optValue = typeof opt === "string" ? opt : opt.value;
              const optLabel = typeof opt === "string" ? opt : opt.label;
              return (
                <li key={optValue}>
                  <button
                    type="button"
                    className="w-full px-3 py-2.5 text-left text-sm text-slate-800 hover:bg-amber-50"
                    onClick={() => add(optValue)}
                  >
                    {optLabel}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * BMS-style category / sub-category / genre / language fields.
 */
export default function CategorySubcategoryFields({
  category,
  subcategories = [],
  genres = [],
  languages = [],
  onCategoryChange,
  onSubcategoriesChange,
  onGenresChange,
  onLanguagesChange,
  required = true,
  showErrors = false,
  disabled = false,
  showGenre = true,
  showLanguage = true,
}) {
  const subs = subcategoriesFor(category);
  const genreOpts = genresFor(category);

  const categoryError =
    showErrors && required && !category ? "Please select an event category" : "";
  const subcategoryError =
    showErrors && required && category && !(subcategories?.length)
      ? "Please provide event sub category"
      : "";
  const genreError =
    showErrors && required && category && showGenre && !(genres?.length)
      ? "Please provide event genre"
      : "";

  return (
    <div className="space-y-4">
      <SingleSearchSelect
        label="Event category"
        required={required}
        placeholder="Select a category"
        options={EVENT_CATEGORIES}
        value={category}
        disabled={disabled}
        error={categoryError}
        onSelect={(v) => {
          onCategoryChange(v);
          onSubcategoriesChange?.([]);
          onGenresChange?.([]);
        }}
        onClear={() => {
          onCategoryChange("");
          onSubcategoriesChange?.([]);
          onGenresChange?.([]);
        }}
      />

      <MultiSearchSelect
        label="Event sub category"
        required={required}
        placeholder="Select a sub category"
        options={subs}
        values={subcategories}
        disabled={disabled || !category}
        error={subcategoryError}
        emptyHint={category ? "No sub categories found" : "Select a category first"}
        onChange={onSubcategoriesChange}
      />

      {showGenre && (
        <MultiSearchSelect
          label="Event genre"
          required={required}
          placeholder="Select a genre"
          options={genreOpts}
          values={genres}
          disabled={disabled || !category}
          error={genreError}
          emptyHint={category ? "No genres found" : "Select a category first"}
          onChange={onGenresChange}
        />
      )}

      {showLanguage && (
        <MultiSearchSelect
          label="Language (optional)"
          required={false}
          placeholder="Select upto 3 languages"
          options={EVENT_LANGUAGES}
          values={languages}
          disabled={disabled}
          max={3}
          onChange={onLanguagesChange}
        />
      )}
    </div>
  );
}
